import { FC, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CatalogPlacement, CreditPack, Face } from '../ApiTypes';
import { getCreditPacks, getFaces } from '../api';
import { getFaceCroppedSrc } from '../Storage';
import CreditPackOffer from '../components/CreditPackOffer';
import SceneCatalog from '../components/SceneCatalog';
import { useUserStateContext } from '../contexts/UserStateContext';
import { EmptyMessage, LoadingMessage } from '../styles/Messages';
import { removeDuplicates, useAsyncEffect } from '../utils';

const CATALOG_LIMIT = 48;

// 選んでいた顔を覚えておく。次に開いたとき、顔の一覧が届くのを待たずに
// タイルを描き始められる（待つと開くたびに読み込み中が挟まる）。
const LAST_FACE_KEY = "catalogLastFaceId";

const readRememberedFaceId = () => {
  try {
    return localStorage.getItem(LAST_FACE_KEY);
  } catch {
    return null;
  }
};

const rememberFaceId = (faceId: string) => {
  try {
    localStorage.setItem(LAST_FACE_KEY, faceId);
  } catch {
    // 覚えられなくても表示には影響しない。
  }
};

const Container = styled.div`
  max-width: 1100px;
  margin: 0px auto;
  padding: 24px 20px 80px 20px;
  color: #dcdcdc;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0px;
`;

const Description = styled.div`
  margin-top: 8px;
  font-size: 15px;
  color: #a8a8a8;
  line-height: 1.6;
`;

const FaceSection = styled.div`
  margin-top: 22px;
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #8a8a8a;
  margin-bottom: 10px;
`;

const FaceRow = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const FaceButton = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  padding: 0px;
  border: 2px solid ${props => props.$active ? 'rgb(254, 167, 0)' : '#3a3a3a'};
  border-radius: 50%;
  background: none;
  cursor: pointer;
  line-height: 0;
`;

const FaceImg = styled.img`
  width: 62px;
  height: 62px;
  border-radius: 50%;
  object-fit: cover;
`;

const AddFaceLink = styled(Link)`
  color: rgb(254, 167, 0);
  font-weight: 600;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
`;

const BulkLink = styled.button`
  flex-shrink: 0;
  padding: 0px;
  border: none;
  background: none;
  color: rgb(254, 167, 0);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`;

const FacePlaceholder = styled.div`
  width: 62px;
  height: 62px;
  border-radius: 50%;
  background-color: #2b2b2b;
  flex-shrink: 0;
`;

/**
 * 常設のカタログ。顔を切り替えると棚全体がその人で埋まる。
 * 「作りたいものを思いついたから来る」ではなく「棚を見に来る」を成立させる面。
 */
const CatalogPage: FC<{}> = () => {
  const userState = useUserStateContext();
  const [faces, setFaces] = useState<Face[]|null>(null);
  const [faceId, setFaceId] = useState<string|null>(() => readRememberedFaceId());
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [showPacks, setShowPacks] = useState(false);

  useEffect(() => {
    if (!userState.isSigned) {
      return;
    }
    getCreditPacks().then(setPacks).catch(() => setPacks([]));
  }, [userState.isSigned]);

  useAsyncEffect(async () => {
    if (!userState.isSigned) {
      setFaces([]);
      return;
    }
    const loaded = removeDuplicates((await getFaces()).filter(face => face.hash), face => face.hash);
    setFaces(loaded);
    // 覚えていた顔が消えている場合だけ最新の顔に寄せる。
    setFaceId(current => (current && loaded.some(face => face.faceId == current))
      ? current
      : (loaded.length > 0 ? loaded[0].faceId : null));
  }, [userState]);

  useEffect(() => {
    if (faceId) {
      rememberFaceId(faceId);
    }
  }, [faceId]);

  return <Container>
    <HeaderRow>
      <Title><Trans>Scenes</Trans></Title>
      {/* タイルを押した瞬間の提案とは別に、能動的にまとめ買いできる入口。
          棚より前に価格を出したくないので、見出しの右端に寄せておく。 */}
      {packs.length > 0 && <BulkLink onClick={() => setShowPacks(true)}>
        <Trans>Buy videos in bulk</Trans>
      </BulkLink>}
    </HeaderRow>
    <Description>
      <Trans>Every scene here already stars the face you pick. Choose one and start streaming.</Trans>
    </Description>

    {faces != null && faces.length == 0 && <FaceSection>
      <EmptyMessage>
        <Trans>Upload a face to fill this shelf with her.</Trans>&nbsp;
        <AddFaceLink to="/create"><Trans>Upload a face</Trans></AddFaceLink>
      </EmptyMessage>
    </FaceSection>}

    {/* 顔の一覧が届く前も、覚えている顔のぶんは枠だけ確保して並びが飛ばないようにする。 */}
    {(faces == null || faces.length > 0) && <FaceSection>
      <SectionLabel><Trans>Face</Trans></SectionLabel>
      <FaceRow>
        {(faces ?? []).map(face => <FaceButton key={face.faceId} $active={face.faceId == faceId} onClick={() => setFaceId(face.faceId)}>
          <FaceImg src={getFaceCroppedSrc(face)} alt="" />
        </FaceButton>)}
        {faces == null && <FacePlaceholder />}
      </FaceRow>
    </FaceSection>}

    {/* 顔の一覧を待たずにタイルを描き始める（覚えている顔で先に出す）。 */}
    {faceId && <SceneCatalog
      faceId={faceId}
      placement={CatalogPlacement.HOME}
      showSortTabs
      limit={CATALOG_LIMIT} />}

    {showPacks && <CreditPackOffer packs={packs} onClose={() => setShowPacks(false)} />}
  </Container>;
};

export default CatalogPage;
